var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import { DataFrame } from "../../model/DataFrame";
let CompareTable = class CompareTable extends LitElement {
    constructor() {
        super(...arguments);
        this.vocabulary = {
            dataframe: msg("DataFrame", { id: 'compare.vocab.dataframe', desc: 'Vocabulary: singular DataFrame' }),
            dataframes: msg("DataFrames", { id: 'compare.vocab.dataframes', desc: 'Vocabulary: plural DataFrames' }),
            rows: msg("rows", { id: 'compare.vocab.rows', desc: 'Vocabulary: plural rows' }),
            row: msg("row", { id: 'compare.vocab.row', desc: 'Vocabulary: singular row' }),
            columns: msg("columns", { id: 'compare.vocab.columns', desc: 'Vocabulary: plural columns' }),
            column: msg("column", { id: 'compare.vocab.column', desc: 'Vocabulary: singular column' }),
        };
        this.mergeMethod = 'add';
        this.comparison = null;
    }
    getComparisons() {
        console.log('Getting comparisons:', this.comparison);
        return this.comparison;
    }
    compareDataFrames() {
        if (!this.oldDataFrame || !this.newDataFrame) {
            return;
        }
        this.comparison = this.newDataFrame.compare(this.oldDataFrame);
    }
    updated(changedProperties) {
        if (changedProperties.has('oldDataFrame') || changedProperties.has('newDataFrame')) {
            this.compareDataFrames();
            this.requestUpdate();
        }
    }
    handleMethodChange(event) {
        this.mergeMethod = event.detail;
        this.dispatchEvent(new CustomEvent('merge-method-change', {
            detail: this.mergeMethod,
            bubbles: true,
            composed: true,
        }));
    }
    getResumeText() {
        const vocab = this.vocabulary;
        switch (this.mergeMethod) {
            case 'add':
                return msg(str `You will add ${this.comparison.addedRows?.rowCount() ?? 0} ${vocab.rows}.`, { id: 'compare.resume.add', desc: 'Resume sentence for the add-only merge method' });
            case 'update':
                return msg(str `You will add ${this.comparison.addedRows?.rowCount() ?? 0} ${vocab.rows}, update ${this.comparison.modifiedRows?.rowCount() ?? 0} ${vocab.rows}, remove ${this.comparison.removedRows?.rowCount() ?? 0} ${vocab.rows}, add ${this.comparison.addedColumns?.length ?? 0} ${vocab.columns}, modify ${this.comparison.modifiedColumns?.length ?? 0} ${vocab.columns} and remove ${this.comparison.removedColumns?.length ?? 0} ${vocab.columns}.`, { id: 'compare.resume.update', desc: 'Resume sentence for the update merge method' });
            case 'replace':
                return msg(str `You will replace the ${this.oldDataFrame.rowCount()} existing ${vocab.rows} with ${this.newDataFrame.rowCount()} new ${vocab.rows}, and replace the ${this.oldDataFrame.columns.length} existing ${vocab.columns} with ${this.newDataFrame.columns.length} new ${vocab.columns}.`, { id: 'compare.resume.replace', desc: 'Resume sentence for the replace merge method' });
            default:
                return '';
        }
    }
    getDetailRows(rows) {
        if (!rows || rows.rowCount() === 0) {
            return msg('No change.', { id: 'compare.detail.noChange', desc: 'Shown when a comparison detail has no change' });
        }
        const firstRows = rows.getFirstRows(5);
        // liste index des lignes modifiées
        const indexList = firstRows.rows.map(row => row[this.oldDataFrame.index_column]);
        let detail = `${indexList.join(', ')}`;
        if (rows.rowCount() > 5) {
            detail += msg(str `, and ${rows.rowCount() - 5} more.`, { id: 'compare.detail.andMore', desc: 'Suffix listing how many additional items are not shown' });
        }
        return detail;
    }
    getDetailColumns(columns) {
        if (!columns || columns.length === 0) {
            return msg('No change.', { id: 'compare.detail.noChange', desc: 'Shown when a comparison detail has no change' });
        }
        const firstColumns = columns.slice(0, 5);
        const columnNames = firstColumns.map(col => col.alias);
        let detail = `${columnNames.join(', ')}`;
        if (columns.length > 5) {
            detail += msg(str `, and ${columns.length - 5} more.`, { id: 'compare.detail.andMore', desc: 'Suffix listing how many additional items are not shown' });
        }
        return detail;
    }
    render() {
        if (!this.oldDataFrame || !this.newDataFrame) {
            return html `<div>${msg('Please provide both DataFrames to compare.', { id: 'compare.error.missingDataFrames', desc: 'Missing DataFrames error' })}</div>`;
        }
        if (!this.oldDataFrame.index_column || !this.newDataFrame.index_column) {
            return html `<div>${msg('Both DataFrames must have an index column for comparison.', { id: 'compare.error.noIndexColumn', desc: 'Missing index column error' })}</div>`;
        }
        if (!this.comparison) {
            return html `<div>${msg('Comparison is in progress or unavailable.', { id: 'compare.error.unavailable', desc: 'Comparison unavailable error' })}</div>`;
        }
        this.getComparisons();
        return html `
            <h3>${msg('DataFrame Comparison', { id: 'compare.title', desc: 'Comparison title' })}</h3>
            <div class="attributes comparison">
                <kpi-card class="kpi"
                    label=${msg('Columns added', { id: 'compare.columns.added', desc: 'Columns added' })}
                    .value=${this.comparison.addedColumns?.length ?? 0}
                    .detail=${this.getDetailColumns(this.comparison.addedColumns)}
                ></kpi-card>
                <kpi-card class="kpi"
                    label=${msg('Columns removed', { id: 'compare.columns.removed', desc: 'Columns removed' })}
                    .value=${this.comparison.removedColumns?.length ?? 0}
                    .detail=${this.getDetailColumns(this.comparison.removedColumns)}
                ></kpi-card>
                <kpi-card class="kpi"
                    label=${msg('Columns modified', { id: 'compare.columns.modified', desc: 'Columns modified' })}
                    .value=${this.comparison.modifiedColumns?.length ?? 0}
                    .detail=${this.getDetailColumns(this.comparison.modifiedColumns)}
                ></kpi-card>
                <kpi-card class="kpi"
                    label=${msg('Columns unchanged', { id: 'compare.columns.unchanged', desc: 'Columns unchanged' })}
                    .value=${this.comparison.unchangedColumns?.length ?? 0}
                    .detail=${this.getDetailColumns(this.comparison.unchangedColumns)}
                ></kpi-card>
            </div>
            <div class="elements comparison">
                <kpi-card class="kpi"
                    label=${msg('Rows added', { id: 'compare.rows.added', desc: 'Rows added' })}
                    .value=${this.comparison.addedRows?.rowCount() ?? 0}
                    .detail=${this.getDetailRows(this.comparison.addedRows)}
                ></kpi-card>
                <kpi-card class="kpi"
                    label=${msg('Rows removed', { id: 'compare.rows.removed', desc: 'Rows removed' })}
                    .value=${this.comparison.removedRows?.rowCount() ?? 0}
                    .detail=${this.getDetailRows(this.comparison.removedRows)}
                ></kpi-card>
                <kpi-card class="kpi"
                    label=${msg('Rows modified', { id: 'compare.rows.modified', desc: 'Rows modified' })}
                    .value=${this.comparison.modifiedRows?.rowCount() ?? 0}
                    .detail=${this.getDetailRows(this.comparison.modifiedRows)}
                ></kpi-card>
                <kpi-card class="kpi"
                    label=${msg('Rows unchanged', { id: 'compare.rows.unchanged', desc: 'Rows unchanged' })}
                    .value=${this.comparison.unchangedRows?.rowCount() ?? 0}
                    .detail=${this.getDetailRows(this.comparison.unchangedRows)}
                ></kpi-card>
            </div>
            <h3>${msg('Merge Method', { id: 'compare.mergeMethod', desc: 'Merge method title' })}</h3>
            <basic-card class="merge-summary-card">
                <p class="resume">${this.getResumeText()}</p>
            </basic-card>
            <div class="merge-method-options">
                <radio-cards
                    .value=${this.mergeMethod}
                    @radio-change=${this.handleMethodChange}
                    .options=${[
            { id: 'add', label: msg('Add only', { id: 'compare.merge.addOnly', desc: 'Add only option' }), icon: new URL('../../assets/add.png', import.meta.url).href },
            { id: 'update', label: msg('Update', { id: 'compare.merge.update', desc: 'Update option' }), icon: new URL('../../assets/update.png', import.meta.url).href },
            { id: 'replace', label: msg('Replace', { id: 'compare.merge.replace', desc: 'Replace option' }), icon: new URL('../../assets/replace.png', import.meta.url).href },
        ]}
                ></radio-cards>
            </div>
        `;
    }
};
CompareTable.styles = css `
        :host {
            display: block;
            width: 100%;
            text-align: left;
        }
        .comparison {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
            margin-bottom: 0.75rem;
        }

        .kpi {
            flex: 1 1 160px;
            min-height: 60px;
            padding: 0.5rem;
        }

        .merge-summary-card {
            background-color: #fdbe479d;
            margin-bottom: 1rem;
        }

        .resume {
            margin: 0;
            font-weight: bold;
        }

        @media (max-width: 900px) {
            .kpi { flex: 1 1 140px; min-height: 55px; }
        }

        @media (max-width: 600px) {
            .kpi { flex: 1 1 100%; }
        }
    `;
__decorate([
    property({ type: Object })
], CompareTable.prototype, "oldDataFrame", void 0);
__decorate([
    property({ type: Object })
], CompareTable.prototype, "newDataFrame", void 0);
__decorate([
    property({ type: Object })
], CompareTable.prototype, "vocabulary", void 0);
__decorate([
    state()
], CompareTable.prototype, "mergeMethod", void 0);
CompareTable = __decorate([
    localized(),
    customElement("compare-table")
], CompareTable);
export { CompareTable };
//# sourceMappingURL=CompareTable.js.map